import React, { useState, useEffect } from 'react';
import Modal from './Modal';
import Confirm from './Confirm';
import Toast from './Toast';

export default function Team() {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [roleFilter, setRoleFilter] = useState('all');
  const [toast, setToast] = useState(null);
  const [modalContent, setModalContent] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState(null);
  const [inviteForm, setInviteForm] = useState({ name: '', email: '', role: 'editor' });
  const [editingMember, setEditingMember] = useState(null);
  const [editForm, setEditForm] = useState({ avatar: '', bio: '', position: '', displayOnFrontend: true, rank: 0 });

  useEffect(() => {
    fetchTeam();
  }, []);

  const fetchTeam = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch('/api/users', {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Failed to fetch');

      const data = await response.json();
      setMembers(data);
    } catch (error) {
      console.error('Failed to fetch team:', error);
      showToast('Failed to load team members');
    } finally {
      setLoading(false);
    }
  };

  const showToast = (msg, type = 'default') => {
    setToast({ message: msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  const filteredMembers = members.filter(m => {
    const roleMatch = roleFilter === 'all' || m.role === roleFilter;
    return roleMatch;
  });

  const stats = {
    total: members.length,
    admins: members.filter(m => m.role === 'admin').length,
    editors: members.filter(m => m.role === 'editor').length
  };

  const formatDate = (date) => {
    if (!date) return 'Never';
    const d = new Date(date);
    return d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
  };

  const handleInvite = async () => {
    if (!inviteForm.name || !inviteForm.email) {
      showToast('Name and email required');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/users', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          name: inviteForm.name,
          email: inviteForm.email,
          password: Math.random().toString(36).slice(-8),
          role: inviteForm.role
        })
      });

      if (!response.ok) throw new Error('Failed to invite');

      const newMember = await response.json();
      setMembers([...members, newMember]);
      setModalContent(null);
      setInviteForm({ name: '', email: '', role: 'editor' });
      showToast('Invitation sent to ' + inviteForm.email);
    } catch (error) {
      showToast('Error inviting teammate');
    }
  };

  const handleUpdateRole = async (memberId, newRole) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/users/${memberId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ role: newRole })
      });

      if (!response.ok) throw new Error('Failed to update');

      const updated = await response.json();
      setMembers(members.map(m => m._id === memberId ? updated : m));
      showToast('Role updated');
    } catch (error) {
      showToast('Error updating role');
    }
  };

  const handleEditMember = (member) => {
    setEditingMember(member._id);
    setEditForm({
      avatar: member.avatar || '',
      bio: member.bio || '',
      position: member.position || '',
      displayOnFrontend: member.displayOnFrontend !== false,
      rank: member.rank || 0
    });
  };

  const handleSaveEdit = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/users/${editingMember}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(editForm)
      });

      if (!response.ok) throw new Error('Failed to update');

      const updated = await response.json();
      setMembers(members.map(m => m._id === editingMember ? updated : m));
      setModalContent(null);
      setEditingMember(null);
      showToast('Member updated');
    } catch (error) {
      showToast('Error updating member');
    }
  };

  const handleDeleteMember = (memberId, memberName) => {
    setConfirmDialog({
      title: `Remove ${memberName}?`,
      description: 'This member will lose access to the admin portal.',
      danger: true,
      onConfirm: async () => {
        try {
          const token = localStorage.getItem('token');
          await fetch(`/api/users/${memberId}`, {
            method: 'DELETE',
            headers: { 'Authorization': `Bearer ${token}` }
          });
          setMembers(members.filter(m => m._id !== memberId));
          showToast(`${memberName} removed from team`);
        } catch (error) {
          showToast('Error removing member');
        }
        setConfirmDialog(null);
      },
      onCancel: () => setConfirmDialog(null)
    });
  };

  if (loading) {
    return (
      <div className="admin-page">
        <div style={{ textAlign: 'center', padding: '40px', color: 'var(--graphite-2)' }}>
          Loading team...
        </div>
      </div>
    );
  }

  return (
    <div className="admin-page">
      <div className="admin-page__head">
        <div>
          <h1 className="admin-page__title">Team & roles <em>· members</em></h1>
          <div className="admin-page__sub">
            <span><strong>{stats.total}</strong> total</span>
            <span><strong>{stats.admins}</strong> admins</span>
            <span><strong>{stats.editors}</strong> editors</span>
          </div>
        </div>
        <div className="admin-page__actions">
          <button className="btn-ghost" onClick={() => setModalContent('roles')}>Roles & permissions</button>
          <button className="btn-primary" onClick={() => setModalContent('invite')}>+ Invite teammate</button>
        </div>
      </div>

      <div className="admin-toolbar">
        <div className="admin-filters">
          <select className="filter-select" value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)}>
            <option value="all">Role — All</option>
            <option value="admin">Admins</option>
            <option value="editor">Editors</option>
            <option value="viewer">Viewers</option>
          </select>
        </div>
      </div>

      <table className="projects-table" style={{ marginTop: '20px' }}>
        <thead>
          <tr>
            <th className="cell-check"><input type="checkbox" /></th>
            <th>Person</th>
            <th>Role</th>
            <th>Joined</th>
            <th className="cell-actions"></th>
          </tr>
        </thead>
        <tbody>
          {filteredMembers.length > 0 ? (
            filteredMembers.map((member) => (
              <tr key={member._id}>
                <td className="cell-check"><input type="checkbox" /></td>
                <td>
                  <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                    <div style={{ width: '32px', height: '32px', borderRadius: '50%', backgroundColor: 'var(--ivory)', color: 'var(--ink)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 600, fontSize: '12px', flexShrink: 0 }}>
                      {member.name.split(' ').map(n => n[0]).join('')}
                    </div>
                    <div>
                      <div style={{ fontWeight: 500 }}>{member.name}</div>
                      <div style={{ fontSize: '12px', color: 'var(--graphite-2)' }}>{member.email}</div>
                    </div>
                  </div>
                </td>
                <td>
                  <select
                    value={member.role}
                    onChange={(e) => handleUpdateRole(member._id, e.target.value)}
                    style={{
                      fontSize: '11px',
                      padding: '2px 6px',
                      backgroundColor: 'rgba(139,42,31,0.2)',
                      color: '#E8A5A5',
                      border: '1px solid rgba(139,42,31,0.3)',
                      borderRadius: '2px',
                      cursor: 'pointer'
                    }}
                  >
                    <option value="admin">Admin</option>
                    <option value="editor">Editor</option>
                    <option value="viewer">Viewer</option>
                  </select>
                </td>
                <td style={{ fontSize: '12px', color: 'var(--graphite-2)' }}>{formatDate(member.createdAt)}</td>
                <td className="cell-actions">
                  <div style={{ position: 'relative', display: 'inline-block' }}>
                    <button
                      onClick={() => setModalContent(`menu-${member._id}`)}
                      style={{ background: 'none', border: 'none', color: 'var(--graphite-2)', cursor: 'pointer' }}
                    >
                      ⋯
                    </button>
                    {modalContent === `menu-${member._id}` && (
                      <div style={{
                        position: 'absolute',
                        right: 0,
                        top: '100%',
                        background: 'var(--ink-2)',
                        border: '1px solid rgba(244,241,234,0.08)',
                        borderRadius: '4px',
                        minWidth: '120px',
                        zIndex: 1000
                      }}>
                        <button
                          onClick={() => {
                            handleEditMember(member);
                            setModalContent('edit');
                          }}
                          style={{
                            display: 'block',
                            width: '100%',
                            padding: '8px 12px',
                            background: 'none',
                            border: 'none',
                            color: 'var(--ivory)',
                            fontSize: '12px',
                            textAlign: 'left',
                            cursor: 'pointer',
                            borderBottom: '1px solid rgba(244,241,234,0.08)'
                          }}
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => {
                            setModalContent(null);
                            handleDeleteMember(member._id, member.name);
                          }}
                          style={{
                            display: 'block',
                            width: '100%',
                            padding: '8px 12px',
                            background: 'none',
                            border: 'none',
                            color: '#E85D5D',
                            fontSize: '12px',
                            textAlign: 'left',
                            cursor: 'pointer'
                          }}
                        >
                          Delete
                        </button>
                      </div>
                    )}
                  </div>
                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan="5" style={{ textAlign: 'center', padding: '40px', color: 'var(--graphite-2)' }}>
                No team members found
              </td>
            </tr>
          )}
        </tbody>
      </table>

      {modalContent === 'invite' && (
        <Modal onClose={() => setModalContent(null)}>
          <div className="modal__head">
            <h3 className="modal__title">Invite teammate</h3>
            <p className="modal__sub">Add a new team member to the admin portal</p>
            <button className="modal__close" onClick={() => setModalContent(null)}>✕</button>
          </div>
          <div className="modal__form">
            <label className="field">
              <span className="field__label">Name</span>
              <input
                type="text"
                className="field__input"
                value={inviteForm.name}
                onChange={(e) => setInviteForm({ ...inviteForm, name: e.target.value })}
                placeholder="Full name"
              />
            </label>
            <label className="field">
              <span className="field__label">Email</span>
              <input
                type="email"
                className="field__input"
                value={inviteForm.email}
                onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
                placeholder="name@example.com"
              />
            </label>
            <label className="field">
              <span className="field__label">Role</span>
              <select
                className="field__input"
                value={inviteForm.role}
                onChange={(e) => setInviteForm({ ...inviteForm, role: e.target.value })}
              >
                <option value="viewer">Viewer</option>
                <option value="editor">Editor</option>
                <option value="admin">Admin</option>
              </select>
            </label>
          </div>
          <div className="modal__actions">
            <button className="btn-ghost" onClick={() => setModalContent(null)}>Cancel</button>
            <button className="btn-primary" onClick={handleInvite}>Send invite</button>
          </div>
        </Modal>
      )}

      {modalContent === 'edit' && editingMember && (
        <Modal onClose={() => setModalContent(null)}>
          <div className="modal__head">
            <h3 className="modal__title">Edit member</h3>
            <p className="modal__sub">Update member profile information</p>
            <button className="modal__close" onClick={() => setModalContent(null)}>✕</button>
          </div>
          <div className="modal__form">
            <label className="field">
              <span className="field__label">Avatar URL</span>
              <input
                type="text"
                className="field__input"
                value={editForm.avatar}
                onChange={(e) => setEditForm({ ...editForm, avatar: e.target.value })}
                placeholder="https://example.com/image.jpg"
              />
            </label>
            <label className="field" style={{ display: 'block', marginTop: '12px', marginBottom: '12px', padding: '12px', backgroundColor: 'rgba(244,241,234,0.05)', borderRadius: '4px', border: '1px dashed rgba(244,241,234,0.2)' }}>
              <span className="field__label">Upload image</span>
              <input
                type="file"
                accept="image/*"
                onChange={(e) => {
                  const file = e.target.files[0];
                  if (file) {
                    const reader = new FileReader();
                    reader.onload = (evt) => {
                      setEditForm({ ...editForm, avatar: evt.target.result });
                    };
                    reader.readAsDataURL(file);
                  }
                }}
                style={{ marginTop: '8px' }}
              />
            </label>
            <label className="field">
              <span className="field__label">Position</span>
              <input
                type="text"
                className="field__input"
                value={editForm.position}
                onChange={(e) => setEditForm({ ...editForm, position: e.target.value })}
                placeholder="e.g. Senior Engineer"
              />
            </label>
            <label className="field">
              <span className="field__label">Bio</span>
              <textarea
                className="field__input"
                value={editForm.bio}
                onChange={(e) => setEditForm({ ...editForm, bio: e.target.value })}
                placeholder="Brief bio or description"
                style={{ minHeight: '80px', resize: 'vertical' }}
              />
            </label>
            <label className="field" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <input
                type="checkbox"
                checked={editForm.displayOnFrontend}
                onChange={(e) => setEditForm({ ...editForm, displayOnFrontend: e.target.checked })}
              />
              <span className="field__label" style={{ margin: 0 }}>Display on frontend</span>
            </label>
            <label className="field">
              <span className="field__label">Rank (for ordering)</span>
              <input
                type="number"
                className="field__input"
                value={editForm.rank}
                onChange={(e) => setEditForm({ ...editForm, rank: parseInt(e.target.value) || 0 })}
                placeholder="0"
              />
            </label>
          </div>
          <div className="modal__actions">
            <button className="btn-ghost" onClick={() => setModalContent(null)}>Cancel</button>
            <button className="btn-primary" onClick={handleSaveEdit}>Save changes</button>
          </div>
        </Modal>
      )}

      {modalContent === 'roles' && (
        <Modal onClose={() => setModalContent(null)}>
          <div className="modal__head">
            <h3 className="modal__title">Roles & permissions</h3>
            <p className="modal__sub">Define what each role can do in the admin portal</p>
            <button className="modal__close" onClick={() => setModalContent(null)}>✕</button>
          </div>
          <div className="modal__form">
            {[
              {
                role: 'Admin',
                description: 'Full access to all features and settings',
                permissions: ['View all content', 'Edit & publish', 'Manage team', 'Access settings']
              },
              {
                role: 'Editor',
                description: 'Create and manage content',
                permissions: ['View all content', 'Edit & publish', 'Create new items', 'View analytics']
              },
              {
                role: 'Viewer',
                description: 'View-only access',
                permissions: ['View all content', 'View analytics']
              }
            ].map((r) => (
              <div key={r.role} style={{ marginBottom: '24px', paddingBottom: '24px', borderBottom: '1px solid rgba(244,241,234,0.08)' }}>
                <h4 style={{ fontSize: '13px', fontWeight: 600, color: 'var(--ivory)', marginBottom: '4px' }}>{r.role}</h4>
                <p style={{ fontSize: '12px', color: 'var(--graphite-2)', marginBottom: '12px' }}>{r.description}</p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {r.permissions.map((perm) => (
                    <div key={perm} style={{ fontSize: '12px', color: 'var(--ivory)', display: 'flex', gap: '8px', alignItems: 'center' }}>
                      <span style={{ color: '#6FCF97' }}>✓</span> {perm}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
          <div className="modal__actions">
            <button className="btn-primary" onClick={() => setModalContent(null)}>Close</button>
          </div>
        </Modal>
      )}

      {confirmDialog && (
        <Confirm
          title={confirmDialog.title}
          description={confirmDialog.description}
          danger={confirmDialog.danger}
          onConfirm={confirmDialog.onConfirm}
          onCancel={confirmDialog.onCancel}
        />
      )}

      {toast && <Toast message={toast.message} type={toast.type} />}
    </div>
  );
}
